import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Search, User, FileText, Plus, Minus, X, AlertCircle , RotateCcw} from 'lucide-react';
import patientService from '../../patient/services/patientService';

const AdminPatientDocumentsPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();

  // State for patient data
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);

  // State for search inputs  
  const [searchData, setSearchData] = useState({
    patientId: '',
    patientName: '',
    patientDOB: ''
  });

  // State for patient search and selection
  const [showPatientIdDropdown, setShowPatientIdDropdown] = useState(false);
  const [showPatientNameDropdown, setShowPatientNameDropdown] = useState(false);
  const [filteredPatientsById, setFilteredPatientsById] = useState([]);
  const [filteredPatientsByName, setFilteredPatientsByName] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isPatientSelected, setIsPatientSelected] = useState(false);

  // State for real patient data from API
  const [allPatients, setAllPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);

  // State for toast notifications
  const [toast, setToast] = useState(null);

  // State for collapsible search section (closed by default)
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  // Mock documents data organized by categories
  const documentsData = {
    medications: [
      { id: 1, name: 'Prescription #001', date: '2024-01-15' },
      { id: 2, name: 'Prescription #002', date: '2024-01-10' },
      { id: 3, name: 'Medication List', date: '2024-01-08' },
      { id: 4, name: 'Dosage Chart', date: '2024-01-05' },
      { id: 5, name: 'Pharmacy Report', date: '2024-01-03' },
      { id: 6, name: 'Side Effects Report', date: '2024-01-01' },
      { id: 7, name: 'Drug Interaction', date: '2023-12-28' },
      { id: 8, name: 'Allergy Report', date: '2023-12-25' }
    ],
    pathology: [
      { id: 9, name: 'Blood Test Results', date: '2024-01-12' },
      { id: 10, name: 'Urine Analysis', date: '2024-01-10' },
      { id: 11, name: 'Biopsy Report', date: '2024-01-08' },
      { id: 12, name: 'Culture Results', date: '2024-01-05' },
      { id: 13, name: 'Histology Report', date: '2024-01-03' },
      { id: 14, name: 'Lab Panel Results', date: '2024-01-01' }
    ],
    imaging: [
      { id: 15, name: 'X-Ray Chest', date: '2024-01-14' },
      { id: 16, name: 'MRI Brain Scan', date: '2024-01-12' }
    ],
    referrals: [
      { id: 17, name: 'Cardiology Referral', date: '2024-01-13' },
      { id: 18, name: 'Orthopedic Referral', date: '2024-01-11' },
      { id: 19, name: 'Dermatology Referral', date: '2024-01-09' },
      { id: 20, name: 'Neurology Referral', date: '2024-01-07' }
    ]
  };

  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Admin Patient Files';
    if (patientId) {
      fetchPatientData();
    }
    fetchAllPatients();
  }, [patientId]);

  // Update search fields when patient data is loaded
  useEffect(() => {
    if (patient) {
      setSearchData({
        patientId: patient.patient_id || '',
        patientName: `${patient.first_name} ${patient.last_name}`,
        patientDOB: patient.dob || ''
      });
      setSelectedPatient(patient);
      setIsPatientSelected(true);
    }
  }, [patient]);

  // Fetch patient data
  const fetchPatientData = async () => {
    try {
      setLoading(true);
      const patientData = await patientService.getPatientByPatientId(patientId);
      console.log('📋 Patient data received:', patientData);
      
      setPatient(patientData);
    } catch (error) {
      console.error('❌ Error fetching patient data:', error);
      showToast('Failed to load patient data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all patients from the database
  const fetchAllPatients = async () => {
    try {
      setLoadingPatients(true);
      console.log('🔍 Fetching all patients from database...');
      
      const patientsData = await patientService.getAllPatients();
      console.log('📋 All patients data received:', patientsData);
      
      if (patientsData && Array.isArray(patientsData)) {
        setAllPatients(patientsData);
      } else {
        console.log('⚠️ No patients data or invalid format');
        setAllPatients([]);
      }
    } catch (error) {
      console.error('❌ Error fetching patients:', error);
      setAllPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  };

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Handle patient ID input changes
  const handlePatientIdChange = (value) => {
    setSearchData(prev => ({
      ...prev,
      patientId: value
    }));

    if (value.trim()) {
      const filtered = allPatients.filter(patient => 
        patient.patient_id?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredPatientsById(filtered.slice(0, 10));
      setShowPatientIdDropdown(true);
    } else {
      setFilteredPatientsById([]);
      setShowPatientIdDropdown(false);
    }
    setShowPatientNameDropdown(false);
  };

  // Handle patient name input changes
  const handlePatientNameChange = (value) => {
    setSearchData(prev => ({
      ...prev,
      patientName: value
    }));

    if (value.trim()) {
      const filtered = allPatients.filter(patient => {
        const fullName = `${patient.first_name} ${patient.last_name}`.toLowerCase();
        return fullName.includes(value.toLowerCase());
      });
      setFilteredPatientsByName(filtered.slice(0, 10));
      setShowPatientNameDropdown(true);
    } else {
      setFilteredPatientsByName([]);
      setShowPatientNameDropdown(false);
    }
    setShowPatientIdDropdown(false);
  };

  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    console.log('🔍 Search submitted:', searchData);
    
    if (searchData.patientId.trim()) {
      // Search by patient ID
      const patient = allPatients.find(p => p.patient_id === searchData.patientId.trim());
      if (patient) {
        handlePatientSelect(patient);
      } else {
        showToast('Patient not found with this ID', 'error');
      }
    } else if (searchData.patientName.trim()) {
      // Search by patient name
      const patient = allPatients.find(p => {
        const fullName = `${p.first_name} ${p.last_name}`.toLowerCase();
        return fullName.includes(searchData.patientName.toLowerCase());
      });
      if (patient) {
        handlePatientSelect(patient);
      } else {
        showToast('Patient not found with this name', 'error');
      }
    }
  };

  // Handle patient selection from dropdown
  const handlePatientSelect = (selectedPatient) => {
    console.log('👤 Patient selected:', selectedPatient);
    
    setSearchData({
      patientId: selectedPatient.patient_id,
      patientName: `${selectedPatient.first_name} ${selectedPatient.last_name}`,
      patientDOB: selectedPatient.dob || ''
    });
    
    setSelectedPatient(selectedPatient);
    setIsPatientSelected(true);
    setShowPatientIdDropdown(false);
    setShowPatientNameDropdown(false);
    setFilteredPatientsById([]);
    setFilteredPatientsByName([]);
    
    // Navigate to the selected patient's documents page
    navigate(`/admin/patientfiles/${selectedPatient.patient_id}/documents`);
  };

  // Handle new patient search (clear selection)
  const handleNewPatient = () => {
    console.log('🆕 New Patient button clicked - clearing search form');
    setSearchData({
      patientId: '',
      patientName: '',
      patientDOB: ''
    });
    setSelectedPatient(null);
    setIsPatientSelected(false);
    setShowPatientIdDropdown(false);
    setShowPatientNameDropdown(false);
    setFilteredPatientsById([]);
    setFilteredPatientsByName([]);
  };

  // Document card component
  const DocumentCard = ({ document }) => (
    <div className="bg-white border border-gray-200 rounded-md p-2 hover:shadow-md transition-shadow cursor-pointer">
      <div className="flex flex-col items-center space-y-1">
        {/* PDF Icon */}
        <div className="w-8 h-8 bg-red-100 rounded-md flex items-center justify-center">
          <FileText className="h-5 w-5 text-red-600" />
        </div>
        {/* File Name */}
        <div className="text-center">
          <p className="text-xs font-medium text-gray-900 mb-0.5 leading-tight">{document.name}</p>
          <p className="text-xs text-gray-500">{document.date}</p>
        </div>
      </div>
    </div>
  );

  // Category section component
  const CategorySection = ({ title, documents, color = "blue" }) => (
    <div className="mb-8">
      <div className={`bg-${color}-50 border border-${color}-200 rounded-lg p-4`}>
        <h3 className={`text-lg font-semibold text-${color}-800 mb-4`}>{title}</h3>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 2xl:grid-cols-12 gap-3">
          {documents.map((document) => (
            <DocumentCard key={document.id} document={document} />
          ))}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-2">
            <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-gray-600">Loading patient data...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Patient Not Found</h2>
          <p className="text-gray-600 mb-4">The patient with ID {patientId} could not be found.</p>
          <Link
            to="/doctor/patientfiles"
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Back to Patient Files
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 p-4 rounded-md shadow-lg z-50 ${
          toast.type === 'error' ? 'bg-red-500' : 'bg-green-500'
        } text-white`}>
          <div className="flex items-center space-x-2">
            {toast.type === 'error' ? (
              <AlertCircle className="h-5 w-5" />
            ) : (
              <FileText className="h-5 w-5" />
            )}
            <span>{toast.message}</span>
          </div>
        </div>
      )}

      {/* Top Banner */}
      <div className="bg-blue-600 text-white px-6 py-1.5 rounded-lg mb-6">
        <h1 className="text-lg font-semibold text-center">Patient Files</h1>
      </div>

      {/* White Content Container with Search Block */}
      <div className={`bg-white rounded-lg shadow-md mb-4 ${isSearchExpanded ? "p-4" : "p-3"}`}>
        {/* Search Section */}
        <div className={isSearchExpanded ? "mb-4" : "mb-2"}>
          {/* Search Header with Icon and Toggle */}
          <div className={`flex items-center justify-between ${isSearchExpanded ? "mb-2" : "mb-0"}`}>
            <div className="flex items-center space-x-2">
              <Search className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-bold text-gray-800">Patient Search</h2>
            </div>
            <button
              type="button"
              onClick={() => setIsSearchExpanded(!isSearchExpanded)}
              className={`bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center justify-center transition-colors shadow-sm ${isSearchExpanded ? "w-8 h-8" : "w-7 h-7"}`}
            >
              {isSearchExpanded ? (
                <Minus className="h-4 w-4" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </button>
          </div>
          
          {/* Collapsible Content */}
          {isSearchExpanded && (
            <div>
              {/* Blue stylish divider */}
              <div className="h-0.5 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full mb-4"></div>
              
              {/* Search Form */}
              <form onSubmit={handleSearch} className="space-y-2">
                {/* Input Fields and Buttons Row - All in One Line */}
                <div className="flex items-end space-x-3">
                  {/* Patient ID Input */}
                  <div className="flex-1 relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Patient ID:</label>
                    <input
                      type="text"
                      placeholder="Enter Patient ID"
                      value={searchData.patientId}
                      onChange={(e) => handlePatientIdChange(e.target.value)}
                      onFocus={() => setShowPatientIdDropdown(true)}
                      disabled={isPatientSelected}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                    />
                    
                    {/* Dropdown for patient selection by ID */}
                    {showPatientIdDropdown && filteredPatientsById.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                        {filteredPatientsById.map((patient) => (
                          <div
                            key={patient.id || patient.patient_id}
                            onClick={() => handlePatientSelect(patient)}
                            className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                          >
                            <div className="font-medium text-gray-900">{patient.patient_id}</div>
                            <div className="text-sm text-gray-600">{patient.first_name} {patient.last_name}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Patient Name Input */}
                  <div className="flex-1 relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name:</label>
                    <input
                      type="text"
                      placeholder="Enter Patient Name"
                      value={searchData.patientName}
                      onChange={(e) => handlePatientNameChange(e.target.value)}
                      onFocus={() => setShowPatientNameDropdown(true)}
                      disabled={isPatientSelected}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                    />
                    
                    {/* Dropdown for patient selection by name */}
                    {showPatientNameDropdown && filteredPatientsByName.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                        {filteredPatientsByName.map((patient) => (
                          <div
                            key={patient.id || patient.patient_id}
                            onClick={() => handlePatientSelect(patient)}
                            className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                          >
                            <div className="font-medium text-gray-900">{patient.first_name} {patient.last_name}</div>
                            <div className="text-sm text-gray-500">ID: {patient.patient_id}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Patient DOB Input */}
                  <div className="w-48">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Patient DOB:</label>
                    <input
                      type="date"
                      value={searchData.patientDOB}
                      onChange={(e) => setSearchData(prev => ({ ...prev, patientDOB: e.target.value }))}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm"
                    />
                  </div>
                  
                  {/* Search Button */}
                  <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 rounded-md transition-colors duration-200 flex items-center space-x-2 shadow-sm hover:shadow-md h-8 text-sm"
                  >
                    <Search className="h-3 w-3" />
                    <span>Search</span>
                  </button>
                  
                  {/* Reset Fields Button */}
                  <button
                    type="button"
                    onClick={handleNewPatient}
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 hover:bg-blue-600 text-white px-4 py-1.5 rounded-md transition-colors duration-200 flex items-center space-x-2 shadow-sm hover:shadow-md h-8 text-sm"
                  >
                <RotateCcw className="h-3 w-3" />
                <span>Reset Fields</span>
              </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Patient Name Header */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 rounded-lg mb-6 shadow-sm relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-24 h-24 bg-blue-200 rounded-full -translate-y-12 translate-x-12"></div>
          <div className="absolute bottom-0 right-0 w-16 h-16 bg-blue-300 rounded-full translate-y-8 translate-x-8"></div>
        </div>
        
        {/* Patient name with enhanced styling */}
        <div className="relative z-10">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-5 bg-blue-500 rounded-full"></div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide leading-tight">
                {patient.first_name} {patient.last_name}
              </h2>
              <div className="h-0.5 w-12 bg-blue-500 mt-1 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Left Navigation Sidebar and Main Content */}
      <div className="flex gap-6">
        <div className="w-64">
          <div className="bg-white rounded-lg shadow-md p-4">
            <nav className="space-y-2">
              <Link 
                to={`/admin/patientfiles/${patientId}/bookings`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                Bookings
              </Link>
              <Link 
                to={`/admin/patientfiles/${patientId}/general_details`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                General Details
              </Link>
              <Link 
                to={`/admin/patientfiles/${patientId}/medical_details`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                Medical Details
              </Link>
              <Link 
                to={`/admin/patientfiles/${patientId}/history`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                History
              </Link>
              <Link 
                to={`/admin/patientfiles/${patientId}/analytics`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                Analytics
              </Link>
              <button className="w-full text-left px-3 py-2 rounded-md bg-blue-600 text-white transition-colors">
                Documents
              </button>
            </nav>
          </div>
        </div>

        {/* Main Content Area - Documents */}
        <div className="flex-1">
          {/* Documents Categories */}
          <CategorySection
            title="Medications"
            documents={documentsData.medications}
            color="blue"
          />
          
          <CategorySection
            title="Pathology"
            documents={documentsData.pathology}
            color="green"
          />
          
          <CategorySection
            title="Imaging"
            documents={documentsData.imaging}
            color="purple"
          />
          
          <CategorySection
            title="Referrals"
            documents={documentsData.referrals}
            color="orange"
          />
        </div>
      </div>
    </div>
  );
};

export default AdminPatientDocumentsPage;
